import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import { buildDeluxeSavePayload } from '../../../../deluxe/credentials/buildDeluxeSavePayload';
import {
  getPlatformDeluxeSettings,
  setPlatformDeluxeSettings,
  testPlatformDeluxeSettings,
  clearPlatformDeluxeSettings,
} from './deluxeCredentialsApi';
import DeluxeEnvFields from './DeluxeEnvFields';

const EMPTY_FORM = {
  clientId: '',
  clientSecret: '',
  accessToken: '',
  signatureKey: '',
  clientIdSandbox: '',
  clientSecretSandbox: '',
  accessTokenSandbox: '',
  signatureKeySandbox: '',
};

const DeluxeCredentialsCard = () => {
  const { showToast } = useNotification();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ configured: false, sandboxConfigured: false });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('idle');
  const [error, setError] = useState('');
  const [envTab, setEnvTab] = useState('production');

  const applyStatus = (data) => {
    const configured = Boolean(data?.configured);
    const sandboxConfigured = Boolean(data?.sandboxConfigured);
    setStatus({ configured, sandboxConfigured });
    setForm(EMPTY_FORM);
    if (!configured && sandboxConfigured) setEnvTab('sandbox');
  };

  useEffect(() => {
    let cancelled = false;
    getPlatformDeluxeSettings()
      .then((data) => {
        if (!cancelled) applyStatus(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err?.message || 'Failed to load Deluxe settings';
          setError(message);
          showToast(message, 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setAction('saving');
    setError('');
    showToast('Saving Deluxe credentials…', 'info');
    try {
      const data = await setPlatformDeluxeSettings(buildDeluxeSavePayload(form));
      applyStatus(data);
      showToast('Deluxe credentials saved.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to save Deluxe settings';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleTest = async (environment) => {
    setLoading(true);
    setAction(environment === 'SANDBOX' ? 'testing-sandbox' : 'testing');
    setError('');
    showToast(`Testing ${environment === 'SANDBOX' ? 'sandbox' : 'production'} Deluxe connection…`, 'info');
    try {
      await testPlatformDeluxeSettings({ environment });
      showToast(
        `${environment === 'SANDBOX' ? 'Sandbox' : 'Production'} Deluxe credentials are valid.`,
        'success'
      );
    } catch (err) {
      const message = err?.message || 'Deluxe test failed';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove the stored Deluxe keys? Website subscriptions will stop until you save new credentials.')) {
      return;
    }
    setLoading(true);
    setAction('clearing');
    setError('');
    showToast('Removing Deluxe credentials…', 'info');
    try {
      await clearPlatformDeluxeSettings();
      applyStatus({ configured: false, sandboxConfigured: false });
      showToast('Deluxe credentials removed.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to disconnect Deluxe';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  return (
    <section className="admin-card app-settings-card platform-credentials-card">
      <div className="platform-credentials-card__header">
        <div>
          <h2 className="platform-credentials-card__title">Deluxe</h2>
          <p className="app-settings-help">
            SaaS billing uses Embedded Payments. Access Token and Signature Key are required. Client ID and Client Secret are optional.
          </p>
        </div>
        <div className="platform-credentials-status-row">
          <span className={`platform-credentials-status ${status.configured ? 'is-connected' : ''}`}>
            {status.configured ? 'Connected' : 'Not connected'}
          </span>
          {status.sandboxConfigured && (
            <span className="platform-credentials-status is-connected">Sandbox connected</span>
          )}
        </div>
      </div>

      {error && (
        <div className="pkg-dialog-alert pkg-dialog-alert--error" role="alert">
          {error}
        </div>
      )}

      <form className="platform-credentials-form" onSubmit={handleSave}>
        <div className="platform-credentials-env-tabs" role="tablist" aria-label="Deluxe environment">
          <button
            type="button"
            role="tab"
            aria-selected={envTab === 'production'}
            className={`platform-credentials-env-tabs__tab${envTab === 'production' ? ' is-active' : ''}`}
            onClick={() => setEnvTab('production')}
          >
            Production
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={envTab === 'sandbox'}
            className={`platform-credentials-env-tabs__tab${envTab === 'sandbox' ? ' is-active' : ''}`}
            onClick={() => setEnvTab('sandbox')}
          >
            Sandbox
          </button>
        </div>

        <div hidden={envTab !== 'production'}>
          <DeluxeEnvFields
            prefix="production"
            title="Production"
            form={{
              clientId: form.clientId,
              clientSecret: form.clientSecret,
              accessToken: form.accessToken,
              signatureKey: form.signatureKey,
            }}
            onChange={(field) => handleChange(field)}
            configured={status.configured}
          />
        </div>
        <div hidden={envTab !== 'sandbox'}>
          <DeluxeEnvFields
            prefix="sandbox"
            title="Sandbox"
            help="For local website signup testing. Paste the sandbox keys Deluxe sent."
            form={{
              clientId: form.clientIdSandbox,
              clientSecret: form.clientSecretSandbox,
              accessToken: form.accessTokenSandbox,
              signatureKey: form.signatureKeySandbox,
            }}
            onChange={(field) => handleChange(`${field}Sandbox`)}
            configured={status.sandboxConfigured}
          />
        </div>

        <div className="platform-credentials-actions">
          <button type="submit" className="pkg-btn pkg-btn-primary" disabled={loading}>
            {action === 'saving' ? 'Saving…' : 'Save credentials'}
          </button>
          <button type="button" className="pkg-btn pkg-btn-secondary" onClick={() => handleTest('PRODUCTION')} disabled={loading || !status.configured}>
            {action === 'testing' ? 'Testing…' : 'Test production'}
          </button>
          <button type="button" className="pkg-btn pkg-btn-secondary" onClick={() => handleTest('SANDBOX')} disabled={loading || !status.sandboxConfigured}>
            {action === 'testing-sandbox' ? 'Testing…' : 'Test sandbox'}
          </button>
          {(status.configured || status.sandboxConfigured) && (
            <button type="button" className="pkg-btn pkg-btn-danger" onClick={handleDisconnect} disabled={loading}>
              {action === 'clearing' ? 'Removing…' : 'Disconnect'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default DeluxeCredentialsCard;
